// @flow
import {toFormData} from 'modules/common/helpers/form';
import type {AuthToken} from 'modules/common/services/auth-token';
import queryString from 'query-string';
import type {TError, TErrors, TResponseError, TResponseSuccess} from 'types';

type TRequestQueryParameters = $ReadOnly<{
    [string]: string | number | $ReadOnlyArray<string | number> | null | void,
}>;

type TRequestBody = $ReadOnly<{
    [string]: any,
}>;

type TRequestExtraOptions = $ReadOnly<{
    dataType?: 'JSON' | 'FORM_DATA',
    isPrivate?: boolean,
}>;

type TRequestParams = $ReadOnly<{|
    body?: TRequestBody,
    extraOptions?: TRequestExtraOptions,
    options?: $ReadOnly<RequestOptions>,
    query?: TRequestQueryParameters,
|}>;

/**
 * Сервис для отправки HTTP запросов
 */
export class HttpService {
    /**
     * Адрес хоста, на который отправляются запросы
     */
    _host: string;

    /**
     * Сервис для работы с токеном авторизации
     */
    _authToken: AuthToken;

    /**
     * Режим запроса
     * @see https://developer.mozilla.org/ru/docs/Web/API/Request/mode
     */
    _mode: 'cors' | 'no-cors' | 'same-origin';

    /**
     * @param host Адрес хоста, на который отправляются запросы
     * @param authToken Сервис для работы с токеном авторизации
     */
    constructor(host: string, authToken: AuthToken) {
        this._host = this.formatHost(host);
        this._authToken = authToken;
        this._mode = 'cors';
    }

    /**
     * Формирует url для запроса
     * @param pathname Часть пути относительно хоста
     * @param queryParams Параметры
     * @returns Полный путь
     */
    getUrl(pathname: string, queryParams: TRequestQueryParameters = {}) {
        const query = queryParams ? queryString.stringify(queryParams) : null;
        const baseUrl = this._host + pathname;
        return query ? `${baseUrl}?${query}` : baseUrl;
    }

    /**
     * Формирует параметры для запроса
     * @param method Метод запроса
     * @param body Тело запроса
     * @param requestOptions Параметры запроса
     * @param extraRequestOptions Дополнитенльные параметры запроса
     * @returns Параметры запроса
     */
    getOptions(
        method: string,
        body?: TRequestBody,
        requestOptions: $ReadOnly<RequestOptions> = {},
        extraRequestOptions: $ReadOnly<TRequestExtraOptions> = {dataType: 'JSON', isPrivate: false}
    ) {
        const options: RequestOptions = {
            method,
            mode: this._mode,
            ...requestOptions,
        };

        options.headers = this.getHeaders(requestOptions.headers, extraRequestOptions.isPrivate);

        if (body) {
            options.body = this.getBody(body, extraRequestOptions.dataType);
        }

        return options;
    }

    /**
     * Формирует заголовки запроса
     * @param headersObj Заголовки запроса
     * @param isPrivate Флаг, обозначающий необходимость добавлять заголовок Authorization
     * @returns Заголовки запроса
     */
    getHeaders(headersObj?: HeadersInit, isPrivate?: boolean) {
        const headers = new Headers(headersObj);

        if (isPrivate) {
            const token = this._authToken.get();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            headers.set('X-Frame-Options', 'allow-from https://cito-back-stage.userstory.ru/ https://cito-front-stage.userstory.ru/');
            headers.set('X-XSS-Protection', '1; mode=block');
        }

        return headers;
    }

    /**
     * Формирует тело запроса
     * @param body тело запроса
     * @param dataType тип тела запроса
     * @returns Отформатированное тело запроса
     */
    getBody(body: TRequestBody, dataType?: string) {
        if ('FORM_DATA' === dataType) {
            return toFormData(body);
        }

        return JSON.stringify(body);
    }

    /**
     * Форматирует адрес хоста, убирая лишние символы
     * @param host Адрес хоста
     * @returns Адрес хоста
     */
    formatHost(host: string) {
        const hostTrimmed = host.trim();
        return hostTrimmed.endsWith('/') ? hostTrimmed.slice(0, -1) : hostTrimmed;
    }

    /**
     * Форматирует успешный ответ
     * @param data Тело ответа
     * @returns Успешный результат запроса
     */
    formatSuccess<T>(data: T): TResponseSuccess<T> {
        return {
            data,
            success: true,
        };
    }

    /**
     * Форматирует неуспешный ответ
     * @param errors массив ошибок
     * @returns Неуспешный результат запроса
     */
    formatError(errors: TErrors): TResponseError {
        const formattedErrors = errors.reduce((sum: {}, item: TError) => {
            if (!(item.data && Object.keys(item.data).length)) {
                return {
                    ...sum,
                    [item.code]: `${item.title}: ${item.detail}`,
                };
            }
            const error = Object.keys(item.data).reduce((result: {}, key: string) => {
                return {
                    ...result,
                    [key]: item.data[key],
                };
            }, {});
            return {
                ...sum,
                ...error,
            };
        }, {});
        return {
            errors: formattedErrors,
            success: false,
        };
    }

    /**
     * Отправляет запрос
     * @param method Метод запроса
     * @param pathname Часть пути относительно хоста
     * @param requestParams Параметры запроса
     * @returns Результат запроса
     */
    async request<T>(
        method: string,
        pathname: string,
        {body, extraOptions, options, query}: TRequestParams = {}
    ): Promise<TResponseSuccess<T> | TResponseError> {
        const path = this.getUrl(pathname, query);
        const requestOptions = this.getOptions(method, body, options, extraOptions);
        try {
            const response = await fetch(path, requestOptions);
            if (response.ok) {
                const {data}: {data: T} = await response.json();
                return this.formatSuccess<T>(data);
            }
            const {errors} = await response.json();
            return this.formatError(errors);
        } catch (error) {
            return this.formatError([error]);
        }
    }

    /**
     * Отправляет GET запрос
     * @param pathname Часть пути относительно хоста
     * @param requestParams Параметры запроса
     * @returns Результат запроса
     */
    async get<T>(pathname: string, requestParams?: TRequestParams): Promise<TResponseSuccess<T> | TResponseError> {
        return this.request<T>('GET', pathname, requestParams);
    }

    /**
     * Отправляет POST запрос
     * @param pathname Часть пути относительно хоста
     * @param requestParams Параметры запроса
     * @returns Результат запроса
     */
    async post<T>(pathname: string, requestParams?: TRequestParams): Promise<TResponseSuccess<T> | TResponseError> {
        return this.request<T>('POST', pathname, requestParams);
    }
}
